import type { JSX, KeyboardEvent, MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../i18n';
import { LOCALES } from '../../i18n/locales';
import type { AppLocaleDef } from '../../i18n/locales';
import { ModalScrim, stepIndex, useModalFocus } from './Dialog';
import { ModuleLabel } from './Module';

/**
 * Language chooser — a port of `ui/components/LanguagePicker.kt`, which is a
 * Material 3 `ModalBottomSheet` holding one radio row per language.
 *
 * Each language is listed by its **native name**, always in its own script, in
 * every locale. A user whose phone came up in a language they cannot read has to
 * be able to find their own entry — which is also why there are no country
 * flags: a flag is a country, not a language, and for operators in Brazil or
 * China it reads like a setting that changes the region reported to the server
 * rather than the interface language.
 *
 * Two knowing differences from Compose's sheet, both platform, both visible:
 *
 * - **The drag handle is an affordance, not a gesture.** Android's sheet can be
 *   flung down to dismiss; reproducing that means owning `touchmove` on a
 *   scrollable panel, which fights Safari's own edge gestures and its
 *   rubber-banding. The handle stays because it is the thing that says the panel
 *   is dismissible, and the dismissal itself is a tap on the scrim or Escape.
 * - **It pays the home-indicator inset itself** (`env(safe-area-inset-bottom)`,
 *   in `.sheet`). Compose gets that from the window insets; a WebClip is
 *   full-bleed and gets nothing, so the last language row would sit half under
 *   the indicator on a notched iPhone.
 */
export function LanguagePickerSheet({
  current,
  onPick,
  onDismiss,
}: {
  current: AppLocaleDef;
  onPick(tag: AppLocaleDef['tag']): void;
  onDismiss(): void;
}): JSX.Element {
  const { t } = useI18n();
  const { ref, onKeyDown } = useModalFocus(onDismiss);
  const label = t('settings_language');

  // Only a press that lands on the layer itself counts as "outside the sheet" —
  // see the same guard in Dialog.tsx.
  const onBackdropClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) onDismiss();
  };

  /**
   * Arrow keys move focus between rows **without** changing the selection.
   *
   * A radio group normally selects as focus moves, but picking a language here
   * closes the sheet — so selection-follows-focus would mean the first arrow
   * press both switches the app's language and dismisses the panel the user was
   * still reading. WAI-ARIA allows the other pattern for exactly this case:
   * arrows move, Space or Enter commits, which `<button>` already does.
   *
   * Only reachable from an external keyboard; there is nothing to do here for
   * touch or VoiceOver, which walk the rows on their own.
   */
  const onGroupKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    // Read from the DOM rather than from LOCALES, because what matters is which
    // rows are focusable right now and in what order they are painted.
    const rows = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]'));
    const index = stepIndex(
      event.key,
      rows.findIndex((row) => row === document.activeElement),
      rows.length,
    );
    if (index === null) return;

    const next = rows[index];
    if (next === undefined) return;
    event.preventDefault();
    next.focus();
  };

  return createPortal(
    <>
      {/* Scrim and layer are two elements for the reason Dialog.tsx documents:
          `opacity` inherits down, so the panel cannot live inside the scrim. */}
      <ModalScrim />
      <div className="modal-layer modal-layer--bottom" onClick={onBackdropClick}>
        <div
          className="sheet"
          role="dialog"
          aria-modal="true"
          aria-label={label}
          tabIndex={-1}
          ref={ref}
          onKeyDown={onKeyDown}
        >
          <div className="sheet__handle" aria-hidden="true" />
          <ModuleLabel text={label} className="sheet__label" />
          <div role="radiogroup" onKeyDown={onGroupKeyDown}>
            {LOCALES.map((locale) => {
              const selected = locale.tag === current.tag;
              return (
                <button
                  key={locale.tag}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  // Roving tabindex: the group is one Tab stop, not four. It also
                  // decides where the sheet opens with focus — `useModalFocus`
                  // takes the first *tabbable* control, which is by construction
                  // the language currently in use.
                  tabIndex={selected ? 0 : -1}
                  className={selected ? 'lang-row lang-row--selected' : 'lang-row'}
                  onClick={() => onPick(locale.tag)}
                >
                  {/* Drawn, not an <input type="radio">: Safari's native radio
                      cannot be recoloured and `accent-color` lands on the system
                      blue rather than this app's. The button carries the radio
                      semantics, so the graphic is hidden. */}
                  <span className="lang-row__radio" aria-hidden="true" />
                  {/*
                   * `lang` is not decoration. Without it VoiceOver reads
                   * "Tiếng Việt" and "中文（简体）" with whatever voice the page
                   * is set to, which is unintelligible — and this row is the one
                   * a user who cannot read the current language has to find.
                   */}
                  <span className="t-body-large lang-row__name" lang={locale.tag}>
                    {locale.nativeName}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
